/**
 * The ONE definition of what counts as an LLM referral source.
 *
 * This list used to exist in four places that had to be edited together:
 * AI_HOSTS + AISource + getAISourceName in ai-referrer-tracker.ts, the
 * ALLOWED_SOURCES set in /api/llm-referral, and SOURCE_LABEL in the admin
 * page. Nothing enforced that they agreed. A host added to the detector but
 * missed in the ingest allowlist would be detected client-side, POSTed, and
 * then silently dropped by the route — a failure with no error anywhere.
 *
 * Everything is derived from AI_HOSTS now. Adding a host is a one-line edit
 * here and the detector, the ingest allowlist, and the dashboard labels all
 * follow. Deliberately dependency-free so client components, server routes,
 * and the GA4 reporting client can all import it.
 */

export type AISource =
  | 'chatgpt'
  | 'claude'
  | 'perplexity'
  | 'gemini'
  | 'copilot'
  | 'grok'
  | 'you'
  | 'poe'
  | 'notebooklm'
  | 'other_ai'
  | 'unknown'
  // Legacy values already persisted in contact_requests.ai_referrer_source.
  // Never written again; kept so historical rows stay readable.
  | 'bard'
  | 'bing_chat'

/**
 * LLM hostnames, matched against the referrer's HOSTNAME — never as a
 * substring of the whole URL.
 *
 * The substring approach this replaces produced false positives that inflated
 * the AI numbers: the old fallback matched any referrer containing '/search',
 * which classified ordinary Bing search traffic as AI, and '/ai', which
 * matched unrelated hosts. Hostname equality (or a dot-suffix match for
 * subdomains) makes that class of error impossible.
 *
 * Only hosts where the referrer is genuinely observable are listed. Assistants
 * that strip the referrer, or that are used inside a native app, cannot be
 * detected here at all — see the undercount note on detectAIReferrer().
 */
export const AI_HOSTS: Record<string, AISource> = {
  'chatgpt.com': 'chatgpt',
  'chat.openai.com': 'chatgpt',
  'claude.ai': 'claude',
  'perplexity.ai': 'perplexity',
  'gemini.google.com': 'gemini',
  'notebooklm.google.com': 'notebooklm',
  'copilot.microsoft.com': 'copilot',
  'grok.com': 'grok',
  'x.ai': 'grok',
  'you.com': 'you',
  'poe.com': 'poe',
}

/** Every hostname we can attribute. Feeds the GA4 source filter. */
export const AI_HOSTNAMES: readonly string[] = Object.keys(AI_HOSTS)

/**
 * Sources the public ingest endpoint will accept.
 *
 * Derived from AI_HOSTS so a new host cannot be detected-but-rejected.
 * `other_ai` is added by hand: it is a valid stored value with no host of its
 * own. `unknown` is absent on purpose — it means "not AI-referred" and must
 * never reach the table.
 */
export const ALLOWED_AI_SOURCES: ReadonlySet<string> = new Set<string>([
  ...Object.values(AI_HOSTS),
  'other_ai',
])

/** Display names. Covers legacy values so historical rows still render. */
export const AI_SOURCE_LABELS: Record<AISource, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  perplexity: 'Perplexity',
  gemini: 'Google Gemini',
  notebooklm: 'NotebookLM',
  copilot: 'Microsoft Copilot',
  grok: 'Grok',
  you: 'You.com',
  poe: 'Poe',
  other_ai: 'AI Assistant',
  unknown: 'Unknown',
  // Legacy, still present on historical rows.
  bard: 'Google Gemini',
  bing_chat: 'Bing Chat',
}

/**
 * The same "exact host or subdomain" rule as matchAiHost(), expressed as a
 * regex for consumers that filter server-side instead of testing one hostname
 * at a time (the GA4 Data API's FULL_REGEXP filter).
 *
 * It lives HERE, next to matchAiHost, because the two must agree. A membership
 * test built from AI_HOSTNAMES alone silently loses the subdomain half of the
 * rule: GA4 reports subdomains as distinct sources (observed on this property:
 * l.facebook.com, m.facebook.com, search.google.com), so `www.perplexity.ai`
 * would be dropped by an exact-match list while matchAiHost accepts it.
 *
 * Dots are escaped, so the leading `(.*\.)?` group is a real dot boundary —
 * `pix.ai` cannot match the `x.ai` entry.
 */
export function aiHostRegexp(): string {
  const alternation = AI_HOSTNAMES.map(host =>
    host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  ).join('|')
  return `(.*\\.)?(${alternation})`
}

/** Exact host, or a subdomain of a listed host (www., m., etc.). */
export function matchAiHost(hostname: string): AISource | null {
  const host = hostname.toLowerCase().replace(/\.$/, '')
  for (const [domain, source] of Object.entries(AI_HOSTS)) {
    if (host === domain || host.endsWith(`.${domain}`)) return source
  }
  return null
}

/**
 * Display name for a stored source value.
 *
 * Takes `string`, not `AISource`: callers read this off a database column, and
 * a value written by an older deploy must still render rather than crash the
 * dashboard. Unrecognised values render as 'Unknown'.
 */
export function getAISourceName(source: string): string {
  return AI_SOURCE_LABELS[source as AISource] ?? 'Unknown'
}
